import classnames from 'classnames';
import React, { isValidElement, ReactElement, useState, useEffect, useRef } from 'react';
import {WebUI, fillSpace as fillSpaceFunc, getNid} from "../../wui-core/src/index"
import Dropdown from '../../wui-dropdown/src/index';
import Menu from '../../wui-menu/src/index';
import Icon from '../../wui-icon/src';
import BreadcrumbItem from './BreadcrumbItem';
import { BreadcrumbProps } from './iBreadcrumb';
import { areChildrenEqual } from './utils';

const { Item, SubMenu } = Menu;

const getOverflowItemsIndex = (overflowWidth: number, itemWidthArr: number[], _index: number = 1, maxWidth:number) => {
    const rangeArr = [0];
    itemWidthArr = itemWidthArr.slice(_index);
    itemWidthArr.forEach((w, index) => {
        rangeArr.push(w + rangeArr[index]);
    });
    let _nextIndex = 0
    if (24 + itemWidthArr[0] + itemWidthArr[itemWidthArr.length - 1] > maxWidth) {
        _nextIndex = rangeArr?.length
    } else {
        _nextIndex = rangeArr.findIndex(num => num > overflowWidth + 38 + 5 + rangeArr[1]);
    }
    return _nextIndex ? [_index, _nextIndex] : [];
}

export const defaultProps = {
    separator: '/',
    fillSpace: false,
}

const Breadcrumb: React.FC<BreadcrumbProps> & { Item: typeof BreadcrumbItem } = (props) => {
    const {
        className,
        clsPrefix,
        separator,
        children: propsChildren,
        style = {},
        fillSpace,
        onClick,
        ...restProps
    } = props;

    const [fillSpaceStyle, setFillSpaceStyle] = useState({});
    const [activeKey, setActiveKey] = useState(props.activeKey);
    const [children, setChildren] = useState(propsChildren);
    const [overflowIndex, setOverflowIndex] = useState<number[]>([]);

    const myRef = useRef<HTMLOListElement>(null);
    const allChildNodesWidth = useRef<number>(0);
    const childNodesWidthArr = useRef<number[]>([]);

    useEffect(() => {
        if (fillSpace) {
            setWidthByfillSpace();
            window.addEventListener('resize', setWidthByfillSpace);
            return () => {
                window.removeEventListener('resize', setWidthByfillSpace);
            };
        }
    }, [fillSpace]);

    useEffect(() => {
        if ('activeKey' in props && activeKey !== props.activeKey) {
            setActiveKey(props.activeKey);
        }
        if ('children' in props && !areChildrenEqual(props.children, children)) {
            setChildren(props.children);
            if (props.fillSpace) {
                setOverflowIndex([]);
            }
        }
    }, [props.activeKey, props.children]);

    const setWidthByfillSpace = () => {
        if (!myRef.current) return;
        fillSpaceFunc(myRef.current, undefined, (_element, _parent, _noPaddingMaxW) => {
            let maxWidth: number = 0;
            if (typeof fillSpace === 'number') {
                if (isNaN(_noPaddingMaxW)) {
                    maxWidth = fillSpace;
                } else {
                    maxWidth = fillSpace < _noPaddingMaxW ? fillSpace : _noPaddingMaxW;
                }
            } else {
                maxWidth = _noPaddingMaxW;
            }
            getAllChildNodesWidth(maxWidth, true);
        });
    };

    const getAllChildNodesWidth = (maxWidth: number, flag?: boolean) => {
        if (!myRef.current) return;

        if (flag && typeof flag === 'boolean') {
            childNodesWidthArr.current = [];
            allChildNodesWidth.current = 0;
            const childNodes = Array.prototype.slice.call(myRef.current.childNodes);
            childNodes.forEach((childNode: any) => {
                const width = childNode.offsetWidth;
                childNodesWidthArr.current.push(width);
                allChildNodesWidth.current += width;
            });
        }

        maxWidth = isNaN(maxWidth) ? 0 : maxWidth;

        let elementPaddingLeft: string | number = getComputedStyle(myRef.current, null).paddingLeft;
        elementPaddingLeft = elementPaddingLeft.match(/\d+/) ? Number(elementPaddingLeft.match(/\d+/)![0]) : 0;

        let newOverflowIndex: number[] = [];
        if (allChildNodesWidth.current + 5 > maxWidth - elementPaddingLeft * 2) {
            const widthRange = allChildNodesWidth.current - (maxWidth - elementPaddingLeft * 2);
            newOverflowIndex = getOverflowItemsIndex(widthRange, childNodesWidthArr.current, 1, maxWidth);
        } else if (allChildNodesWidth.current !== 0) {
            newOverflowIndex = [];
        }

        setFillSpaceStyle({ maxWidth: `${maxWidth}px` });
        setOverflowIndex(newOverflowIndex);
    };

    const onItemClick = (e: React.MouseEvent, key: string) => {
        onClick?.(e, key);
    };

    const onItemStrClick = (e: React.MouseEvent) => {
        onClick?.(e);
    };

    const onSelect = (v: any) => {
        onClick?.(v.domEvent, v.key);
    };

    const renderChildren = () => {
        let childNodes: any[] = [];
        React.Children.forEach(children, (item: React.ReactChild) => {
            if (!item) return;
            const itemKey = (item as ReactElement).key;

            if (typeof item === 'string') {
                childNodes.push(<li onClick={onItemStrClick}>{item}</li>);
                return;
            }

            if (isValidElement(item) && item.props && 'separator' in (item.props as object)) {
                const childrenProps = {
                    onItemClick,
                    keyValue: itemKey,
                    isActiveKey: activeKey === itemKey,
                    activeKey
                };
                childNodes.push(React.cloneElement(item, childrenProps));
                return;
            }

            const childrenProps = {
                separator,
                onItemClick,
                keyValue: itemKey,
                isActiveKey: activeKey === itemKey,
                activeKey
            };
            childNodes.push(React.cloneElement(item as ReactElement, childrenProps));
        });

        if (fillSpace && childNodes && overflowIndex.length > 0) {
            const omitItems = childNodes.slice(...overflowIndex);
            const dropdownMenu = (
                <Menu onSelect={onSelect}>
                    {omitItems.map(item => {
                        if (item && 'overlay' in item.props && item.props.overlay) {
                            return (
                                <SubMenu
                                    onClick={(info: any) => {
                                        item.props.onClick?.(info.domEvent);
                                    }}
                                    title={item.props.children}
                                    key={item.key}
                                >
                                    {item.props.overlay?.props.children}
                                </SubMenu>
                            );
                        }
                        return (
                            <Item
                                key={item.key}
                                onClick={(info: any) => {
                                    item.props.onClick?.(info.domEvent);
                                }}
                            >
                                {item.props.children}
                            </Item>
                        )
                    })}
                </Menu>
            );

            const overflowNode = (
                <Dropdown
                    key="dropdown"
                    overlayClassName={`${clsPrefix}-dropdown`}
                    className={`${clsPrefix}-overflow`}
                    overlay={dropdownMenu}
                >
                    <BreadcrumbItem separator={separator} key="overflow">
                        <Icon type="uf-ellipsis" />
                    </BreadcrumbItem>
                </Dropdown>
            );

            childNodes.splice(overflowIndex[0], overflowIndex[1] - overflowIndex[0], overflowNode);
        }

        return childNodes ? [...childNodes] : null;
    };

    const adapterNid = getNid(props);
    const childNodes = renderChildren();

    // 直接类型断言，保证类型安全
    const olProps = restProps as React.OlHTMLAttributes<HTMLOListElement>;

    return (
        <ol
            {...olProps}
            ref={myRef}
            className={classnames(className, clsPrefix)}
            style={{...fillSpaceStyle, ...style}}
            {...adapterNid}
        >
            {childNodes}
        </ol>
    );
};

Breadcrumb.Item = BreadcrumbItem;
export default WebUI({name: "breadcrumb", defaultProps})(Breadcrumb);